import { SvgPlusCircle } from '@bazaar-marketplace/assets/plus-circle';
import { cn } from '@bazaar-marketplace/utils';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatedSticker, Button } from '~/components';
import { RarityBadge } from '~/components/rarity-badge/rarity-badge';
import { ROUTES } from '~/constants';
import { usePacksControllerGetPackById } from '~/lib/api';
import { getRarityBackground } from './assets';
import { addSearchParams } from '~/lib/search-params';

interface PackResultsStepProps {
  packId: string;
  onClose?: () => void;
}

export const PackResultsStep = ({ packId, onClose }: PackResultsStepProps) => {
  const t = useTranslations();
  const router = useRouter();
  const [shouldFadeOut, setShouldFadeOut] = useState<boolean>(false);
  const { data: pack } = usePacksControllerGetPackById(packId);

  useEffect(() => {
    setShouldFadeOut(true);
  }, []);

  const onSuccess = () => {
    if (onClose) {
      onClose();
    } else {
      router.replace(addSearchParams(ROUTES.MY_COLLECTIONS, { packId }));
    }
  };

  return (
    <>
      <div className={cn('fixed z-[1000] inset-0 bg-white pointer-events-none', shouldFadeOut && 'fade-out-slow')} />

      {createPortal(
        <>
          {pack?.data.unwrapped?.rarity ? (
            <Image
              alt="Rarity background"
              className="fixed inset-0 object-cover z-[50]"
              fill
              src={getRarityBackground(pack.data.unwrapped.rarity)}
            />
          ) : null}

          <div className="fixed inset-0 z-[200] flex flex-col items-center justify-center">
            <div className="flex flex-col gap-8 items-center mb-8">
              <RarityBadge rarity={pack?.data.unwrapped?.rarity ?? 'common'} />
              <span className="font-bold text-3xl text-center">{pack?.data.unwrapped?.collectionName}</span>
            </div>

            <div className="w-full max-w-[100vw] overflow-x-auto scroll-smooth snap-x snap-mandatory scrollbar-hide">
              <div className="flex gap-8 pl-[calc(50vw-80px)] pr-[calc(50vw-80px)]">
                {pack?.data.unwrapped?.stickers.map((sticker) => (
                  <AnimatedSticker
                    key={sticker.name}
                    autoplay
                    className="snap-center flex-shrink-0 !size-[160px]"
                    src={sticker.fileName}
                  />
                ))}
              </div>
            </div>
          </div>

          <Button
            className="flex gap-2 text-[#23262E] fixed bottom-8 z-[200] inset-x-4 w-auto"
            variant="rainbow"
            onClick={onSuccess}
          >
            <SvgPlusCircle /> {t('ui.button.add-to-collection')}
          </Button>
        </>,

        document.body,
      )}
    </>
  );
};
